import React, { useEffect } from 'react';
import ChallengeSection from './components/ChallengeSection';
import FeedbackSection from './components/FeedbackSection';
import StatsSection from './components/StatsSection';
import InstallButton from './components/InstallButton';
import { useAppLogic } from './hooks/useAppLogic';


const App: React.FC = () => {
  const {
    isInitialized,
    challengeText,
    status,
    isRecording,
    lastResult,
    stats,
    statsData,
    modeStats,
    modeStatsData,
    selectedStatsMode,
    buttonsState,
    startButtonText,
    transcriptionLogs,
    inputMode,
    currentKey,
    challengeType,
    handleStartContinue,
    handleStop,
    handleResetStats,
    handleResetStatsByMode,
    handleStatsModeChange,
    handleInputModeChange,
    handleChallengeTypeChange,
    handleNoteSelect,
    handleKeySelect
  } = useAppLogic();

  // Determine if we're in a "running" state (should show Stop) or "stopped" state (should show Start/Continue)
  const isRunning = status.state === 'speaking' || status.state === 'recording' || status.state === 'processing' || status.state === 'result';
  const mainButtonText = isRunning ? 'Stop' : startButtonText;
  const mainButtonAction = isRunning ? handleStop : handleStartContinue;
  const mainButtonDisabled = isRunning ? buttonsState.stopDisabled : buttonsState.startDisabled;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!mainButtonDisabled) {
          mainButtonAction();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [mainButtonDisabled, mainButtonAction]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-light text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 p-3 sm:p-4 md:p-6">
      <div className="max-w-5xl mx-auto space-y-3 sm:space-y-4 md:space-y-6">
        {/* Header - Compact on mobile */}
        <header className="text-center mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 mb-1 sm:mb-2">
            Scaleflow
          </h1>
          <p className="text-sm sm:text-base text-slate-700 hidden sm:block">Practice identifying scale degrees across all keys</p>
        </header>

        {/* Challenge Display */}
        <ChallengeSection
          challengeText={challengeText}
          status={status}
          isRecording={isRecording}
          onMainAction={mainButtonAction}
          mainButtonText={mainButtonText}
          mainButtonDisabled={mainButtonDisabled}
          isRunning={isRunning}
          inputMode={inputMode}
          onInputModeChange={handleInputModeChange}
          currentKey={currentKey}
          onNoteSelect={handleNoteSelect}
          challengeType={challengeType}
          onKeySelect={handleKeySelect}
          onChallengeTypeChange={handleChallengeTypeChange}
        />

        {/* Feedback Section */}
        <FeedbackSection lastResult={lastResult} transcriptionLogs={transcriptionLogs} />

        {/* Statistics - hide when game is running */}
        {!isRunning && (
          <StatsSection 
            stats={stats} 
            statsData={statsData} 
            modeStats={modeStats}
            modeStatsData={modeStatsData}
            selectedStatsMode={selectedStatsMode}
            onResetStats={handleResetStats}
            onResetStatsByMode={handleResetStatsByMode}
            onStatsModeChange={handleStatsModeChange}
          />
        )}

        {/* Footer - Compact on mobile */}
        <footer className="text-center text-xs sm:text-sm text-slate-600 pt-3 sm:pt-4 md:pt-6 space-y-2 sm:space-y-3">
          <p className="hidden sm:block">Press Space to {isRunning ? 'stop' : 'start/continue'}</p>
          <div className="flex justify-center">
            <InstallButton />
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;